import { FunctionComponent, useMemo } from "react";
import { GenericError } from "src/components/Feedback/GenericError";
import { GQLRemoteDataView } from "src/components/Layout/RemoteDataView";
import { replaceCIDsWithInlineImages } from "src/components/WysiwygEditor/lexical/utils/converters";
import {
  MESSAGE_TEMPLATE_CATEGORIES,
  MessageTemplateCategory,
} from "src/constants";
import { useMessageRichContentQuery } from "src/hooks/useMessageRichContent";
import { useRemoteDataQuery } from "src/hooks/useRemoteDataQuery";
import { AutomaticMessageFormType } from "src/scenes/orgAdmin/formTemplates/types/editMessagesDraft";
import * as Url from "src/services/url";
import * as GQL from "src/types/graphql";
import { RichContentDocument } from "src/types/messages/content";
import * as RD from "src/types/remoteData";
import { GET_MESSAGE_TEMPLATE } from "../../graphql/queries";
import {
  defaultTemplateRankDisabled,
  defaultTemplateRankEnabled,
} from "../../messageTemplateBody";
import { EditFormTemplateEditMessagesProvider } from "./context";
import {
  EditMessageTemplateForm,
  EditMessageTemplateSkeleton,
} from "./EditMessageTemplateForm";

export const EditMessageTemplate: FunctionComponent = () => {
  const params = Url.OrgAdmin.MessageTemplate.useParams();
  const {
    formTemplateId = "",
    messageTemplateType = "",
    schoolId: schoolIdParam = "",
  } = params;

  const schoolId = schoolIdParam === "standard" ? null : schoolIdParam;
  const templateType = messageTemplateType as GQL.message_template_type_enum;

  const { remoteData } = useRemoteDataQuery<
    GQL.GetMessageTemplate,
    GQL.GetMessageTemplateVariables
  >(GET_MESSAGE_TEMPLATE, {
    variables: {
      formTemplateId: formTemplateId,
      templateType,
      schoolId: schoolId ?? "",
      skipSchool: !schoolId,
    },
    skip: !formTemplateId || !messageTemplateType,
    fetchPolicy: "cache-and-network",
  });

  // Derive contentId from the GraphQL query result
  const contentId = useMemo(() => {
    if (!remoteData.hasData()) return undefined;

    const schoolSpecificEnabled =
      remoteData.data.form_template_by_pk?.school_specific_messages_enabled;
    const messageTemplate =
      schoolSpecificEnabled && schoolId
        ? remoteData.data.message_template_school_specific[0]
        : remoteData.data.message_template_standard[0];

    return messageTemplate?.message_template_rich_contents[0]?.content_id;
  }, [remoteData, schoolId]);

  // Fetch rich content using the query hook
  const { remoteData: richContentRemoteData } = useMessageRichContentQuery(
    contentId,
    !remoteData.hasData() // skip until we have the template data
  );

  const getInitialValues = (
    template:
      | GQL.GetMessageTemplate["message_template_standard"][0]
      | undefined,
    defaultTemplate: typeof defaultTemplateRankEnabled,
    richContentData: RichContentDocument | null | undefined
  ): AutomaticMessageFormType => {
    if (!template) {
      return {
        enabled: false,
        emailSubject: defaultTemplate[templateType].emailSubject,
        emailMarkup: defaultTemplate[templateType].emailHtml,
        emailText: defaultTemplate[templateType].emailText,
        legacyMarkup: defaultTemplate[templateType].emailHtml,
        emailLexical: undefined,
        smsBody: defaultTemplate[templateType].sms,
        smsLexical: undefined,
      };
    }

    return {
      enabled: template.enabled,
      emailSubject: template.email_subject,
      emailMarkup: richContentData?.html
        ? replaceCIDsWithInlineImages(
            richContentData.html,
            richContentData.attachments
          )
        : "",
      emailText: richContentData?.text ?? template.email_plain_text,
      legacyMarkup: richContentData?.html ? undefined : template.email_markup,
      emailLexical: richContentData?.lexical
        ? JSON.stringify(richContentData.lexical)
        : "",
      smsBody: template.sms_body,
      smsLexical: richContentData?.smsLexical
        ? JSON.stringify(richContentData.smsLexical)
        : "",
    };
  };

  return (
    <GQLRemoteDataView
      remoteData={RD.toTuple(remoteData, richContentRemoteData)}
      error={() => <GenericError />}
      loading={<EditMessageTemplateSkeleton />}
    >
      {([data, richContent]) => {
        const schoolSpecificEnabled =
          data.form_template_by_pk?.school_specific_messages_enabled ?? false;
        const template =
          schoolSpecificEnabled && schoolId
            ? data.message_template_school_specific[0]
            : data.message_template_standard[0];
        const rankingEnabled =
          data.form_template_by_pk?.sections[0]?.schools_ranking_section
            ?.ranking_enabled ?? false;
        const defaultTemplate = rankingEnabled
          ? defaultTemplateRankEnabled
          : defaultTemplateRankDisabled;

        const isOfferEnabled =
          data.form_template_by_pk?.lottery_offers_enabled ?? false;
        const isOfferRelated = MESSAGE_TEMPLATE_CATEGORIES.find(
          (category) =>
            category.key === MessageTemplateCategory.LotteryAndOffers
        )?.messageTemplateTypes.includes(templateType);

        const initialValues = getInitialValues(
          template,
          defaultTemplate,
          richContent
        );

        return (
          <EditFormTemplateEditMessagesProvider
            editMessagesInitialValues={initialValues}
            messageTemplateType={templateType}
            formTemplateId={formTemplateId}
          >
            <EditMessageTemplateForm
              isLoading={remoteData.isLoading()}
              existingTemplateId={template?.id}
              existingContentId={getRichContentId(template)}
              schoolSpecificEnabled={schoolSpecificEnabled ?? false}
              schoolName={data.school_by_pk?.name}
              templateDisabled={!isOfferEnabled && isOfferRelated}
            />
          </EditFormTemplateEditMessagesProvider>
        );
      }}
    </GQLRemoteDataView>
  );
};

function getRichContentId(
  template: GQL.GetMessageTemplate["message_template_standard"][0] | undefined
) {
  const rc = template?.message_template_rich_contents ?? [];

  return (
    rc.find((content) => content.language === "en")?.content_id ??
    rc[0]?.content_id
  );
}
